import { useState } from 'react';
import type { RecipientPersonType } from '@api';
import { ActionsCell } from '@common';
import {
  Alert,
  Button,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { RecipientGroupFormValuesType } from '../../types';

const EMAIL_FIELDS = ['TO', 'CC'];

type RecipientsPropsType = {
  availablePersons: RecipientPersonType[];
};

// Edits the "recipients" array of a RecipientGroupFormValuesType form.
export const Recipients: React.FC<RecipientsPropsType> = ({
  availablePersons,
}) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<RecipientGroupFormValuesType>();
  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'recipients',
  });
  const recipients = watch('recipients');
  const [adding, setAdding] = useState<'PERSON' | 'EMAIL' | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string>();
  const [nameError, setNameError] = useState<string>();

  const personOptions = availablePersons
    .filter(
      ({ id }) =>
        !recipients.some((r) => r.kind === 'PERSON' && r.personId === id)
    )
    .map((person) => ({
      value: person.id,
      label: `${person.firstname} ${person.lastname} · ${person.companyLink.name} (${person.email})`,
    }));

  const addPerson = (personId: string | null) => {
    const person = availablePersons.find(({ id }) => id === personId);
    if (!person) return;
    // Persons are listed before external emails.
    const firstCustomIndex = recipients.findIndex((r) => r.kind === 'CUSTOM');
    insert(firstCustomIndex === -1 ? recipients.length : firstCustomIndex, {
      kind: 'PERSON',
      personId: person.id,
      name: `${person.firstname} ${person.lastname}`,
      email: person.email,
      company: person.companyLink.name,
      emailField: 'TO',
    });
  };

  const addEmail = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const isDuplicate = recipients.some(
      (r) => r.email.toLowerCase() === trimmedEmail.toLowerCase()
    );
    const nextNameError = trimmedName
      ? undefined
      : t('REPORTS.RECIPIENT_GROUP_FORM.ERRORS.NAME_REQUIRED');
    const nextEmailError = !z.email().safeParse(trimmedEmail).success
      ? t('REPORTS.RECIPIENT_GROUP_FORM.ERRORS.EMAIL_INVALID')
      : isDuplicate
        ? t('REPORTS.RECIPIENT_GROUP_FORM.ERRORS.EMAIL_DUPLICATE')
        : undefined;

    setNameError(nextNameError);
    setEmailError(nextEmailError);
    if (nextNameError || nextEmailError) return;

    append({
      kind: 'CUSTOM',
      name: trimmedName,
      email: trimmedEmail,
      emailField: 'TO',
    });
    setName('');
    setEmail('');
  };

  const onEmailKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    // The inline row must not submit the surrounding form.
    event.preventDefault();
    addEmail();
  };

  const hasNoTo =
    recipients.length > 0 && !recipients.some((r) => r.emailField === 'TO');

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={500}>{t('REPORTS.RECIPIENT_GROUP_FORM.RECIPIENTS')}</Text>
        <Group gap="xs">
          <Button
            size="xs"
            color="blue"
            variant={adding === 'PERSON' ? 'filled' : 'outline'}
            onClick={() => setAdding(adding === 'PERSON' ? null : 'PERSON')}
          >
            {t('REPORTS.RECIPIENT_GROUP_FORM.ADD_PERSON')}
          </Button>
          <Button
            size="xs"
            color="blue"
            variant={adding === 'EMAIL' ? 'filled' : 'outline'}
            onClick={() => setAdding(adding === 'EMAIL' ? null : 'EMAIL')}
          >
            {t('REPORTS.RECIPIENT_GROUP_FORM.ADD_EMAIL')}
          </Button>
        </Group>
      </Group>
      {adding === 'PERSON' && (
        <Select
          aria-label={t('REPORTS.RECIPIENT_GROUP_FORM.ADD_PERSON')}
          placeholder={t('REPORTS.RECIPIENT_GROUP_FORM.PERSON_PLACEHOLDER')}
          data={personOptions}
          value={null}
          onChange={addPerson}
          disabled={personOptions.length === 0}
          description={
            personOptions.length === 0
              ? t('REPORTS.RECIPIENT_GROUP_FORM.NO_AVAILABLE_PERSONS')
              : undefined
          }
          searchable
        />
      )}
      {adding === 'EMAIL' && (
        <Group align="flex-start" gap="xs">
          <TextInput
            aria-label={t('REPORTS.RECIPIENT_GROUP_FORM.NAME')}
            placeholder={t('REPORTS.RECIPIENT_GROUP_FORM.NAME')}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            onKeyDown={onEmailKeyDown}
            error={nameError}
            flex={1}
          />
          <TextInput
            aria-label={t('REPORTS.RECIPIENT_GROUP_FORM.EMAIL')}
            placeholder={t('REPORTS.RECIPIENT_GROUP_FORM.EMAIL')}
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            onKeyDown={onEmailKeyDown}
            error={emailError}
            flex={1}
          />
          <Button color="blue" variant="outline" onClick={addEmail}>
            {t('REPORTS.RECIPIENT_GROUP_FORM.ADD')}
          </Button>
        </Group>
      )}
      {hasNoTo && (
        <Alert color="orange">
          {t('REPORTS.RECIPIENT_GROUP_FORM.ONLY_CC')}
        </Alert>
      )}
      {fields.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          {t('REPORTS.RECIPIENT_GROUP_FORM.NO_RECIPIENTS')}
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                {t('REPORTS.RECIPIENT_GROUP_FORM.TABLE_COLUMNS.NAME')}
              </Table.Th>
              <Table.Th>
                {t('REPORTS.RECIPIENT_GROUP_FORM.TABLE_COLUMNS.EMAIL')}
              </Table.Th>
              <Table.Th>
                {t('REPORTS.RECIPIENT_GROUP_FORM.TABLE_COLUMNS.COMPANY')}
              </Table.Th>
              <Table.Th>
                {t('REPORTS.RECIPIENT_GROUP_FORM.TABLE_COLUMNS.FIELD')}
              </Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {fields.map((field, index) => (
              <Table.Tr key={field.id}>
                <Table.Td>{field.name}</Table.Td>
                <Table.Td>{field.email}</Table.Td>
                <Table.Td>
                  {field.kind === 'PERSON' ? (
                    field.company
                  ) : (
                    <Text size="sm" c="dimmed">
                      {t('REPORTS.RECIPIENT_GROUP_FORM.EXTERNAL')}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Controller
                    control={control}
                    name={`recipients.${index}.emailField`}
                    render={({ field: { value, onChange } }) => (
                      <SegmentedControl
                        size="xs"
                        data={EMAIL_FIELDS}
                        value={value}
                        onChange={onChange}
                        aria-label={`${field.name} ${t('REPORTS.RECIPIENT_GROUP_FORM.TABLE_COLUMNS.FIELD')}`}
                      />
                    )}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionsCell.Delete onClick={() => remove(index)} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};
